import { alpha, Box, Button, IconButton, Typography } from '@mui/material'
import createDialogTitle from '../../generic/utilities/dialogUtil'
import { SmartDialog } from '../../generic/utilities/SmartDialog'
import SmartMateriaUITable from '../../generic/SmartMateriaUITable'
import ResourceTypesBar from './resourcetypes/ResourceTypesBar'
import { isRowOpen } from './openRowGuard'
import { DeleteOutlineRounded, WindowRounded } from '@mui/icons-material'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import { apiPost, apiGet } from '../../generic/Api_Functions'
import { searchBomVersionStructure } from '../bomLibrary/bomLibraryApi'
import { Group, Panel } from 'react-resizable-panels'
import PanelSeparator from '../../generic/PanelSeparator'
import { buildChangeMgmtToolbar, computeChangeMgmtValues, useWorkflowResourceExtendedColumns } from './ResourcesExtendedColumnsManagement'
import { buildParentCodeByPosition, resolveParentComponentCode } from './bomTree'

/**
 * @typedef {import('../../generic/utilities/objectStructures/workflow/workflow').ResourceQuantity} ResourceQuantity ResourceQuantity object
 * @typedef {import('../../generic/utilities/objectStructures/globalStructures').IdCodeName} IdCodeName IdCodeName object
 */

// ── Pure helpers (exported for unit testing) ──────────────────────────────────
// RN3 / D5: rounding to 4 decimal places. This is the rounding already in force in
// this screen's BOM path; kept as-is (not aligned with Raw Material's 3 decimals).
export const roundTo4 = (value) => Math.round(value * 10000) / 10000

// RN7 / RN6: the "is fractionable" condition must resolve the same way whether the
// row comes from BOM (fractionable/size stored inside resourceQuantityBOM) or was
// just dropped onto the table (fractionable/size stored at the row root) — CA5 is
// the non-regression guarantee for the BOM path. BOM data takes precedence when
// present, since it is the historically-working path.
export const getResourceFractionableInfo = (row) => {
  const bom = row.resourceQuantityBOM || {}
  if (bom.fractionable) {
    return { fractionable: true, size: Number(bom.size) || 0 }
  }
  if (row.fractionable) {
    return { fractionable: true, size: Number(row.size) || 0 }
  }
  return { fractionable: false, size: 0 }
}

// RN1 / RN3: second unit = first unit * size, rounded to 4 decimals.
export const computeSecondUnit = (size, firstUnit) => roundTo4(firstUnit * size)

// RN2 / RN3: first unit = second unit / size, rounded to 4 decimals. Only call
// when size > 0 (V4 guard is enforced by the caller).
export const computeFirstUnit = (size, secondUnit) => roundTo4(secondUnit / size)

// V3: a negative quantity is invalid; message aligned with the Raw Material screen.
export const checkPositiveQuantity = (value, t_) => {
  if (value === '' || value === null || value === undefined) return ''
  return Number(value) < 0 ? t_('Must be positive') : ''
}

/**
 *
 * @param {Object} props
 * @param {boolean} props.editing
 * @param {ResourceQuantity[]} props.resources
 * @param {number} props.partReferenceId
 * @param {function()} props.onResourcesChanged
 * @param {IdCodeName[]} props.resourceTypes
 * @param {import('../../generic/utilities/objectStructures/workflow/workflow').Workflow} props.workflow
 * @param {boolean} [props.showPalette] Whether the draggable component palette is mounted. Defaults to
 *   true, which is the graph's behaviour; the process tree turns it off for a NESTED job, whose material
 *   is shown whole but is not editable here — it is edited in its own process (story #150, D9).
 * @returns
 */
const JobLinkerResources = ({ editing, resources, partReferenceId, onResourcesChanged, resourceTypes, showAlert, parentPartReference, workflow, showPalette = true, onEditingRowChanged = () => {} }) => {
  const { t_ } = useSmartTranslation()
  const [deleteResourceDialog, setDeleteResourceDialog] = useState({ open: false, row: null })
  const { changeMgmtColumns, withExtendedColumnsPrefix } = useWorkflowResourceExtendedColumns(t_)
  const [isDragOver, setIsDragOver] = useState(false)
  const [forceValuesChange, setForceValuesChange] = useState(null)
  // RN4: guards the bidirectional first/second unit sync against re-entrancy. When a
  // field's onChange pushes the derived value to its counterpart (via forceValuesChange),
  // that push re-triggers the counterpart's own onChange; this flag makes that reentrant
  // call skip its own recalculation so the field the user edited stays the immutable
  // master (RN2) instead of being overwritten by the rounded round-trip value.
  // Pattern mirrored from RawMaterial.jsx's isSyncingRef.
  const isSyncingRef = useRef(false)
  // S64/S65 (historia 152, RN-02.1/RN-02.2): the parent assembly of each assigned component, keyed
  // by normalized position. The job's own rows only carry their own `position`, so the immediate
  // parent's CODE has to come from the workflow's OWN BOM — the very list the BOM tab reads
  // (`workflowResource/*`, a plain read: HU-02 writes nothing). Empty until it is loaded, so the
  // column declares "no parent" rather than inventing one.
  const [parentCodeByPosition, setParentCodeByPosition] = useState({})

  const hasWorkflowResources = workflow?.hasWorkflowResources
  const tempWorkflowUUID = workflow?.tempWorkflowUUID
  const workflowId = workflow?.id
  const pendingBomId = workflow?.pendingBomLink?.bomId
  useEffect(() => {
    let cancelled = false
    const loadParentAssemblies = async () => {
      // With a library version chosen but not saved yet the components on screen are ITS
      // components (story 117/S71): the saved workflow's BOM would answer with other positions, so
      // no parent is claimed at all until the link resolves. Never an invented one (RN-02.2).
      if (!hasWorkflowResources || !tempWorkflowUUID || pendingBomId) {
        setParentCodeByPosition({})
        return
      }
      const components = workflowId
        ? await apiGet('workflowResource/' + tempWorkflowUUID + '/' + workflowId)
        : await apiGet('workflowResource/' + tempWorkflowUUID)
      if (!cancelled) {
        setParentCodeByPosition(buildParentCodeByPosition(components))
      }
    }
    loadParentAssemblies()
    return () => { cancelled = true }
  }, [hasWorkflowResources, tempWorkflowUUID, workflowId, pendingBomId])

  /*
   * RN117.3.2: a host that goes away with its row open leaves the guard's set on the way out. Without
   * this, closing the job window — or collapsing the panel that holds this table — with a row open
   * would leave the process impossible to save and nothing on screen to say why, which is worse than
   * the defect the guard exists to fix.
   */
  useEffect(() => () => onEditingRowChanged(false), [onEditingRowChanged])

  const dataFetch = async (pagination, type) => {
    if (type !== 'BOM') {
      if (type === 'PART_REFERENCE') {
        if (partReferenceId) {
          return await apiPost('workflowResourceParts/' + partReferenceId, pagination)
        }
        return await apiPost('workflowResourceParts', pagination)
      }
      return await apiPost('workflowResourceRawMaterials', pagination)
    }
    let data
    if (workflow.pendingBomLink?.bomId) {
      // Story 117 (S71/S135, PO 2026-09-09/10): a library version chosen but not saved yet is the
      // BOM the editor shows, so the components a job can consume are that version's (N4), not the
      // ones `workflowResource/*` still returns for the saved workflow.
      data = await searchBomVersionStructure(workflow.pendingBomLink.bomId, pagination)
    } else if (!workflow.id && workflow.tempWorkflowUUID) {
      data = await apiPost('workflowResource/' + workflow.tempWorkflowUUID, pagination)
    } else {
      data = await apiPost('workflowResource/' + workflow.tempWorkflowUUID + '/' + workflow.id, pagination)
    }
    data.content.forEach(element => {
      let remaining = Number(element.quantity) || 0
      const nodes = workflow.nodes?.filter(node => node.nodeType === 'JOB')
      nodes?.forEach(node => {
        // A job consuming a PART REFERENCE comes back with no resourceQuantityBOM at all (Sun's mapper
        // builds it only for workflow resources and raw material references), so reading through it
        // threw and took the whole palette down while loading a BOM workflow.
        const resources = node.jobReference.resources?.filter(resource => resource.resourceQuantityBOM?.workflowResourceUUID === element.uuid)
        resources?.forEach(resource => {
          remaining = remaining - (Number(resource.quantityFirstUnit) || 0)
        })
      })
      element.remaining = Math.round(remaining * 10000) / 10000
    })
    return data
  }

  const onDragOver = useCallback((event) => {
    event.preventDefault()
    setIsDragOver(true)
    event.dataTransfer.dropEffect = 'move'
  }, [])

  const deleteResource = () => {
    resources.splice(deleteResourceDialog.row['smart-table-index'], 1)
    const newList = [...resources]
    onResourcesChanged(newList)
    setDeleteResourceDialog({ ...deleteResourceDialog, open: false })
  }

  // Check if the structure contains the current part to avoid recursive dependencies
  const structureContainsPart = (structure, currentPartId) => {
    const dfs = (node) => {
      if (node.id === currentPartId) return true
      const children = node.resourceChildren || []
      return children.some(dfs)
    }
    return structure.some(dfs)
  }

  // Validate dropping a part reference resource checking its defaut workflow structure
  const validatePartReferenceDrop = useCallback(
    async (draggedDefaultWorkflow, currentPartId, pa) => {
      if (!draggedDefaultWorkflow) return { ok: true }

      const structure = await apiGet(`workflows/resourceStructure/${draggedDefaultWorkflow}`)

      // Validate against the current partReference (if exists)
      if (currentPartId) {
        const loopSelf = structureContainsPart(structure, currentPartId)
        if (loopSelf) {
          return {
            ok: false,
            msg: t_('This resource would create a recursive dependency and cannot be added.')
          }
        }
      }

      // Validate against the parent partReference (if exists)
      if (parentPartReference?.id) {
        const loopParent = structureContainsPart(structure, parentPartReference.id)
        if (loopParent) {
          return {
            ok: false,
            msg: t_('This resource would create a recursive dependency and cannot be added.')
          }
        }
      }
      return { ok: true }
    },
    [t_, parentPartReference]
  )

  const onDrop = useCallback(async (event) => {
    setIsDragOver(false)
    if (!editing) return
    const itemQuantity = Number(event.dataTransfer.getData('application/reactflow/itemQuantity'))
    const droppedResources = Array.from({ length: itemQuantity }, (_, index) => ({
      resourceType: event.dataTransfer.getData('application/reactflow/resourceType_' + index),
      resourceId: Number(event.dataTransfer.getData('application/reactflow/resourceId_' + index)),
      resourceCode: event.dataTransfer.getData('application/reactflow/code_' + index),
      resourceName: event.dataTransfer.getData('application/reactflow/name_' + index),
      defaultWorkflow: Number(event.dataTransfer.getData('application/reactflow/defaultWorkflow_' + index)) || null,
      workflowResourceUUID: event.dataTransfer.getData('application/reactflow/workflowResourceUUID_' + index),
      engItem: event.dataTransfer.getData('application/reactflow/engItem_' + index),
      engRevision: event.dataTransfer.getData('application/reactflow/engRevision_' + index),
      position: event.dataTransfer.getData('application/reactflow/position_' + index),
      revision: event.dataTransfer.getData('application/reactflow/revision_' + index),
      category: event.dataTransfer.getData('application/reactflow/category_' + index),
      quantity: event.dataTransfer.getData('application/reactflow/quantity_' + index),
      componentType: event.dataTransfer.getData('application/reactflow/componentType_' + index),
      fractionable: event.dataTransfer.getData('application/reactflow/fractionable_' + index) === 'true',
      size: Number(event.dataTransfer.getData('application/reactflow/size_' + index)) || 0,
      unitCode: event.dataTransfer.getData('application/reactflow/unitCode_' + index) || '',
      extendedColumn1: event.dataTransfer.getData('application/reactflow/extendedColumn1_' + index),
      extendedColumn2: event.dataTransfer.getData('application/reactflow/extendedColumn2_' + index),
      extendedColumn3: event.dataTransfer.getData('application/reactflow/extendedColumn3_' + index),
      extendedColumn4: event.dataTransfer.getData('application/reactflow/extendedColumn4_' + index),
      extendedColumn5: event.dataTransfer.getData('application/reactflow/extendedColumn5_' + index),
      extendedColumn6: event.dataTransfer.getData('application/reactflow/extendedColumn6_' + index),
      extendedColumn7: event.dataTransfer.getData('application/reactflow/extendedColumn7_' + index),
      extendedColumn8: event.dataTransfer.getData('application/reactflow/extendedColumn8_' + index),
      extendedColumn9: event.dataTransfer.getData('application/reactflow/extendedColumn9_' + index),
      extendedColumn10: event.dataTransfer.getData('application/reactflow/extendedColumn10_' + index),
      extendedColumn11: event.dataTransfer.getData('application/reactflow/extendedColumn11_' + index),
      extendedColumn12: event.dataTransfer.getData('application/reactflow/extendedColumn12_' + index)
    }))

    let updatedResources = [...resources]
    for (const droppedResource of droppedResources) {
      const {
        resourceType,
        resourceId,
        resourceCode,
        resourceName,
        defaultWorkflow,
        workflowResourceUUID,
        engItem,
        engRevision,
        position,
        revision,
        category,
        quantity,
        componentType,
        fractionable,
        size,
        unitCode,
        extendedColumn1,
        extendedColumn2,
        extendedColumn3,
        extendedColumn4,
        extendedColumn5,
        extendedColumn6,
        extendedColumn7,
        extendedColumn8,
        extendedColumn9,
        extendedColumn10,
        extendedColumn11,
        extendedColumn12
      } = droppedResource
      const type = resourceTypes.find((t) => t.code === resourceType)
      if (!type) {
        console.warn('Dropped resource without a valid resource type', { resourceType, workflowResourceUUID, resourceCode })
        continue
      }
      if (resourceType === 'PART_REFERENCE') {
        const { ok, msg } = await validatePartReferenceDrop(defaultWorkflow, partReferenceId)
        if (!ok) {
          showAlert({
            severity: 'warning',
            message: msg
          })
          return
        }
      }
      if (resourceType === 'RAW_MATERIAL_REFERENCE' && fractionable && !size) {
        showAlert({
          severity: 'error',
          message: t_('The raw material "{{name}}" is fractionable but has no size defined. Please set its size before adding it to the workflow.', { name: resourceName || resourceCode })
        })
        return
      }
      const resourceExists = workflowResourceUUID
        ? resources.find(resource => resource.resourceQuantityBOM.workflowResourceUUID === workflowResourceUUID)
        : resources.find(resource => resource.resourceType.code === resourceType && resource.resource.id === resourceId && resource.resource.code === resourceCode && resource.resource.name === resourceName)

      if (resourceExists) {
        // RN8 / CA6: re-drop increments first unit and keeps second unit paired,
        // using the same fractionable/size resolution as the render and the
        // manual-edit handlers (RN7).
        const { fractionable, size } = getResourceFractionableInfo(resourceExists)
        const nextQuantity = Number(resourceExists.quantityFirstUnit) + 1
        resourceExists.quantityFirstUnit = nextQuantity
        if (fractionable) {
          resourceExists.quantitySecondUnit = computeSecondUnit(size, nextQuantity)
        }
      } else {
        const newItem = {
          id: null,
          resource: {
            id: resourceId,
            code: resourceCode,
            name: resourceName
          },
          resourceType: type,
          quantityFirstUnit: quantity || 1,
          quantitySecondUnit: fractionable ? (quantity || 1) * size : 0,
          fractionable,
          size,
          unitCode,
          resourceQuantityBOM: {
            workflowResourceUUID,
            engItem,
            engRevision,
            position,
            revision,
            category,
            componentType,
            quantity,
            extended_column1: extendedColumn1,
            extended_column2: extendedColumn2,
            extended_column3: extendedColumn3,
            extended_column4: extendedColumn4,
            extended_column5: extendedColumn5,
            extended_column6: extendedColumn6,
            extended_column7: extendedColumn7,
            extended_column8: extendedColumn8,
            extended_column9: extendedColumn9,
            extended_column10: extendedColumn10,
            extended_column11: extendedColumn11,
            extended_column12: extendedColumn12,
            deleted: false
          }
        }
        updatedResources = [...updatedResources, newItem]
      }
    }

    onResourcesChanged(updatedResources)
  }, [
    editing,
    resources,
    onResourcesChanged,
    resourceTypes,
    partReferenceId,
    showAlert,
    validatePartReferenceDrop,
    t_
  ])

  const onValueChanged = (row, field, value) => {
    setForceValuesChange({
      rowsToChange: [{
        'smart-table-index': row['smart-table-index'],
        valuesToChange: [{ field, value }]
      }]
    })
  }

  const onQuantityChange = (row) => {
    // RN4: this call is the reentrant echo of a push we just made from
    // onConsumptionChange below (forceValuesChange -> onChange). Skip it once so the
    // second unit the user typed is not recalculated from the rounded first unit.
    if (isSyncingRef.current) { isSyncingRef.current = false; return }
    const { fractionable, size } = getResourceFractionableInfo(row)
    if (!fractionable) return
    if (row.quantityFirstUnit === '') {
      isSyncingRef.current = true
      onValueChanged(row, 'quantitySecondUnit', '')
      return
    }
    const quantityFirstUnit = parseFloat(row.quantityFirstUnit) || 0
    // V3 / CA7: a negative quantity does not derive the other unit.
    if (quantityFirstUnit < 0) return
    isSyncingRef.current = true
    onValueChanged(row, 'quantitySecondUnit', computeSecondUnit(size, quantityFirstUnit))
  }

  const onConsumptionChange = (row) => {
    // RN4: this call is the reentrant echo of a push we just made from
    // onQuantityChange above (forceValuesChange -> onChange). Skip it once so the first
    // unit the user typed is not recalculated from the rounded second unit.
    if (isSyncingRef.current) { isSyncingRef.current = false; return }
    const { fractionable, size } = getResourceFractionableInfo(row)
    if (!fractionable) return
    if (row.quantitySecondUnit === '') {
      isSyncingRef.current = true
      onValueChanged(row, 'quantityFirstUnit', '')
      return
    }
    // V4 / CA8: guard against division by zero (or a non-positive size).
    if (!(size > 0)) return
    const quantitySecondUnit = parseFloat(row.quantitySecondUnit) || 0
    // V3 / CA7: a negative quantity does not derive the other unit.
    if (quantitySecondUnit < 0) return
    isSyncingRef.current = true
    onValueChanged(row, 'quantityFirstUnit', computeFirstUnit(size, quantitySecondUnit))
  }

  const checkMinimumBatchCondition = (row) => {
    const { fractionable } = getResourceFractionableInfo(row)
    const valid = fractionable
      ? row.quantityFirstUnit > 0 || row.quantitySecondUnit > 0
      : row.quantityFirstUnit > 0
    return valid ? '' : t_('Minimum resource quantity first unit must be greater than 0')
  }

  // V3 / CA7: reject a negative quantity with the "positive" message, on top of the
  // existing minimum-batch condition (which stays on the first-unit column, as before).
  const checkQuantityFirstUnitCondition = (row) => {
    return checkPositiveQuantity(row.quantityFirstUnit, t_) || checkMinimumBatchCondition(row)
  }

  const checkQuantitySecondUnitCondition = (row) => {
    return checkPositiveQuantity(row.quantitySecondUnit, t_)
  }

  const resourceColumns = [
    {
      field: 'resource.code',
      name: t_('Code'),
      readOnly: true
    },
    {
      field: 'resource.name',
      name: t_('Name'),
      readOnly: true
    },
    {
      field: 'resourceType.code',
      name: t_('Type'),
      readOnly: true,
      render: (row) => {
        if (row.resourceType?.code === 'RAW_MATERIAL_REFERENCE') {
          return t_('Raw material')
        }
        if (row.resourceType?.code === 'PART_REFERENCE') {
          return t_('Part reference')
        }
      }
    },
    {
      field: 'quantityFirstUnit',
      name: t_('Quantity first unit'),
      mandatory: true,
      numeric: true,
      decimals: 4,
      onChange: onQuantityChange,
      condition: checkQuantityFirstUnitCondition
    },
    {
      field: 'quantitySecondUnit',
      name: t_('Quantity second unit'),
      mandatory: true,
      numeric: true,
      decimals: 4,
      onChange: onConsumptionChange,
      condition: checkQuantitySecondUnitCondition,
      renderOnlyInReadMode: (row) => getResourceFractionableInfo(row).fractionable,
      render: (row) => {
        if (!getResourceFractionableInfo(row).fractionable) return ''
        return row.unitCode && row.unitCode !== 'undefined' ? `${row.quantitySecondUnit} ${row.unitCode}` : `${row.quantitySecondUnit} unit/s`
      }
    }
  ]

  const getColumnColor = (row) => {
    return row.resourceQuantityBOM.deleted ? 'error.main' : null
  }

  const resourceBOMColumns = [
    {
      field: 'resourceQuantityBOM.engItem',
      name: t_('Engineering item'),
      color: (row) => getColumnColor(row)
    },
    {
      field: 'resourceQuantityBOM.engRevision',
      name: t_('Engineering revision'),
      readOnly: true,
      color: (row) => getColumnColor(row)
    },
    {
      field: 'resource.code',
      name: t_('Code'),
      readOnly: true,
      color: (row) => getColumnColor(row)
    },
    {
      field: 'resource.name',
      name: t_('Name'),
      readOnly: true,
      color: (row) => getColumnColor(row)
    },
    {
      // S64/S65 (historia 152, CA2.1/RN-02.1/RN-02.2, J32): the component's IMMEDIATE parent
      // assembly, derived — never stored on the row, which would make it travel in the save of a
      // read-only piece of information. A first-level component shows nothing at all.
      field: 'parentAssemblyCode',
      name: t_('Parent'),
      readOnly: true,
      render: (row) => (
        <Typography color={getColumnColor(row)}>
          {resolveParentComponentCode(row.resourceQuantityBOM?.position, parentCodeByPosition)}
        </Typography>
      )
    },
    {
      field: 'resourceType.code',
      name: t_('Type'),
      readOnly: true,
      render: (row) => {
        let message = ''
        if (row.resourceType?.code === 'RAW_MATERIAL_REFERENCE') {
          message = t_('Raw material')
        }
        if (row.resourceType?.code === 'PART_REFERENCE') {
          message = t_('Part reference')
        }
        return <Typography color={row.resourceQuantityBOM.deleted ? 'error.main' : null}>{message}</Typography>
      }
    },
    {
      field: 'resourceQuantityBOM.position',
      name: t_('position'),
      color: (row) => getColumnColor(row)
    },
    {
      field: 'resourceQuantityBOM.revision',
      name: t_('Revision'),
      color: (row) => getColumnColor(row)
    },
    {
      field: 'resourceQuantityBOM.category',
      name: t_('Category'),
      color: (row) => getColumnColor(row)
    },
    {
      field: 'resourceQuantityBOM.componentType',
      name: t_('Component type'),
      color: (row) => getColumnColor(row)
    },
    {
      field: 'quantityFirstUnit',
      name: t_('Quantity first unit'),
      mandatory: true,
      numeric: true,
      decimals: 4,
      onChange: onQuantityChange,
      condition: checkQuantityFirstUnitCondition,
      color: (row) => getColumnColor(row)
    },
    {
      field: 'quantitySecondUnit',
      name: t_('Quantity second unit'),
      mandatory: true,
      numeric: true,
      decimals: 4,
      onChange: onConsumptionChange,
      condition: checkQuantitySecondUnitCondition,
      renderOnlyInReadMode: (row) => getResourceFractionableInfo(row).fractionable,
      render: (row) => {
        if (!getResourceFractionableInfo(row).fractionable) return ''
        const message = row.unitCode && row.unitCode !== 'undefined' ? `${row.quantitySecondUnit} ${row.unitCode}` : `${row.quantitySecondUnit} unit/s`
        return <Typography color={row.resourceQuantityBOM.deleted ? 'error.main' : null}>{message}</Typography>
      }
    }
  ]

  const getBomColumns = () => withExtendedColumnsPrefix(resourceBOMColumns, 'resourceQuantityBOM.')

  const changeMgmtValues = useMemo(() => {
    return computeChangeMgmtValues({
      rows: resources,
      changeMgmtColumns,
      valueGetter: (row, col) => row?.resourceQuantityBOM?.[col.field]
    })
  }, [resources, changeMgmtColumns])

  const bomHeaderToolbar = useMemo(() => {
    return buildChangeMgmtToolbar({
      changeMgmtColumns,
      changeMgmtValues,
      enabled: workflow?.hasWorkflowResources
    })
  }, [workflow?.hasWorkflowResources, changeMgmtColumns, changeMgmtValues])

  const resourceActions = {
    edit: {
      fetchFunction: (row) => {
        const newList = [...resources]
        newList[row['smart-table-index']] = row
        onResourcesChanged(newList)
      },
      condition: (row) => editing,
      disabled: (row) => row.resourceQuantityBOM?.deleted
    },
    custom: [
      {
        name: t_('Delete'),
        render: () => (
          <IconButton>
            <DeleteOutlineRounded />
          </IconButton>
        ),
        disabled: !editing,
        onClick: (row) => {
          setDeleteResourceDialog({ open: true, row })
        }
      }
    ]
  }

  const handleDragLeave = (e) => {
    e.preventDefault()
    setIsDragOver(false) // Remove highlight when drag leaves the area
  }

  const containerStyle = {
    position: 'relative', // Crucial for the absolute overlay to attach to this div
    transition: 'all 0.2s ease-in-out'
  }

  return (
    <>
      <SmartDialog
        setOpen={deleteResourceDialog.open} message={t_('Are you sure you want to delete this resource?')} title={createDialogTitle({ title: t_('Delete?'), icon: WindowRounded, color: 'error' })}
        accept={false} cancelCallback={() => setDeleteResourceDialog({ ...deleteResourceDialog, open: false })} close={false} renderCustomButtons={
          <Button color='error' onClick={deleteResource} variant='outlined'>
            <Typography color='error'>{t_('DELETE')}</Typography>
            <WindowRounded color='error' sx={{ fontSize: '1.4em', marginLeft: '0.3rem' }} />
          </Button>
        }
      />
      <Group>
        {showPalette && (
          <Panel defaultSize='25%' minSize='15rem' maxSize='80%'>
            <ResourceTypesBar
              // Remounted when the BOM origin changes (a library version chosen, the temp scope
              // rotated by a save/revert), so the palette re-reads the components.
              key={`${workflow.pendingBomLink?.bomId ?? ''}-${workflow.tempWorkflowUUID ?? ''}-${workflow.hasWorkflowResources ?? ''}`}
              parts={(pagination) => dataFetch(pagination, 'PART_REFERENCE')}
              rawMaterials={(pagination) => dataFetch(pagination, 'RAW_MATERIAL_REFERENCE')}
              workflowResources={(pagination) => dataFetch(pagination, 'BOM')}
              editMode={editing}
              hasWorkflowResources={workflow.hasWorkflowResources}
            />
          </Panel>
        )}
        {showPalette && <PanelSeparator />}
        <Panel
          onDrop={onDrop}
          onDragOver={onDragOver}
          onDragLeave={handleDragLeave}
          style={containerStyle}
        >

          {isDragOver && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                zIndex: 15,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                borderRadius: 2,
                border: 2,
                borderStyle: 'dashed',
                borderColor: 'primary.main',

                bgcolor: (theme) => alpha(theme.palette.background.default, 0.9),

                pointerEvents: 'none'
              }}
            >
              <Typography variant='h5' color='primary' sx={{ fontWeight: 'bold' }}>
                {t_('Drop items here')}
              </Typography>
            </Box>
          )}
          <SmartMateriaUITable
            dataFetch={resources}
            columns={workflow.hasWorkflowResources ? getBomColumns() : resourceColumns}
            actions={resourceActions}
            toolbar={bomHeaderToolbar}
            forceValuesChange={forceValuesChange}
            // D14/RN117.6: it already received the row and threw it away. Reporting it is what makes a
            // half-typed quantity block the save instead of being lost on the next save.
            onEditIndexChanged={(tableIndex, row) => {
              setForceValuesChange(null)
              onEditingRowChanged(isRowOpen(row))
            }}
          />

        </Panel>
      </Group>
    </>
  )
}

export default JobLinkerResources
